import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { userAPI, watchlistAPI, notificationAPI } from '../lib/api';
import { LayoutDashboard, Globe, Gavel, ArrowRightLeft, User, Loader2, ExternalLink, Heart, Bell, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [domains, setDomains] = useState([]);
  const [bids, setBids] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', company: '' });
  const [watchlist, setWatchlist] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    const fetchData = async () => {
      try {
        const [statsRes, domainsRes, bidsRes, txRes, watchlistRes, notifsRes] = await Promise.all([
          userAPI.dashboard(), userAPI.domains(), userAPI.bids(), userAPI.transactions(),
          watchlistAPI.get().catch(() => ({ data: { watchlist: [] } })),
          notificationAPI.get().catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        ]);
        setStats(statsRes.data);
        setDomains(domainsRes.data.domains || []);
        setBids(bidsRes.data.bids || []);
        setTransactions(txRes.data.transactions || []);
        setWatchlist(watchlistRes.data.watchlist || []);
        setNotifications(notifsRes.data.notifications || []);
        setUnreadCount(notifsRes.data.unread_count || 0);
        setProfileForm({ name: user.name || '', phone: '', company: '' });
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, [user, navigate, authLoading]);

  const updateProfile = async () => {
    try {
      await userAPI.updateProfile(profileForm);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update'); }
  };

  if (loading || authLoading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="dashboard-page">
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-zinc-500 mt-1">{t('dashboard.welcome')}, {user?.name}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { icon: Globe, label: t('dashboard.myDomains'), value: stats.domains_count || 0, color: 'text-[#00E5FF]' },
          { icon: Gavel, label: t('dashboard.myBids'), value: stats.bids_count || 0, color: 'text-amber-400' },
          { icon: ArrowRightLeft, label: t('dashboard.transactions'), value: stats.transactions_count || 0, color: 'text-green-400' },
          { icon: LayoutDashboard, label: 'Active Auctions', value: stats.active_auctions || 0, color: 'text-purple-400' },
        ].map((s, i) => (
          <div key={i} className="p-5 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <s.icon className={`h-5 w-5 ${s.color} mb-3`} />
            <div className="text-2xl font-bold text-white font-['Outfit']">{s.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="domains" className="space-y-6">
        <TabsList className="bg-[#0A0A0A] border border-white/5 p-1 rounded-sm">
          <TabsTrigger value="domains" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-domains">
            {t('dashboard.myDomains')}
          </TabsTrigger>
          <TabsTrigger value="bids" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-bids">
            {t('dashboard.myBids')}
          </TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-transactions">
            {t('dashboard.transactions')}
          </TabsTrigger>
          <TabsTrigger value="profile" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-profile">
            {t('dashboard.profile')}
          </TabsTrigger>
          <TabsTrigger value="watchlist" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-watchlist">
            <Heart className="h-3 w-3 mr-1" /> Watchlist
          </TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="tab-notifications">
            <Bell className="h-3 w-3 mr-1" /> {unreadCount > 0 ? `(${unreadCount})` : ''} Alerts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="domains">
          {domains.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <Globe className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No domains listed yet</p>
              <Button onClick={() => navigate('/sell-domain')} className="mt-4 bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="sell-domain-btn">
                Sell a Domain
              </Button>
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.domain')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.price')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.status')}</th>
                    <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr></thead>
                  <tbody>
                    {domains.map(d => (
                      <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-sm text-white font-medium">{d.full_name}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">${d.price?.toLocaleString()}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${d.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-800 text-zinc-500'}`}>{d.status}</span></td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => navigate(`/domains/${d.id}`)} className="text-xs text-[#00E5FF] hover:underline">
                            <ExternalLink className="h-3.5 w-3.5 inline mr-1" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="bids">
          {bids.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <Gavel className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No bids placed yet</p>
              <Button onClick={() => navigate('/auctions')} className="mt-4 bg-[#00E5FF] text-black font-bold rounded-sm">Browse Auctions</Button>
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.domain')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.amount')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.date')}</th>
                  </tr></thead>
                  <tbody>
                    {bids.map(b => (
                      <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-sm text-white">{b.domain_id}</td>
                        <td className="px-4 py-3 text-sm text-[#00E5FF] font-medium">${b.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-zinc-500">{new Date(b.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="transactions">
          {transactions.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <ArrowRightLeft className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No transactions yet</p>
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.type')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.domain')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.amount')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.date')}</th>
                  </tr></thead>
                  <tbody>
                    {transactions.map(tx => (
                      <tr key={tx.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-sm text-white capitalize">{tx.type}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{tx.domain_name}</td>
                        <td className="px-4 py-3 text-sm text-[#00E5FF]">${tx.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${tx.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>{tx.status}</span></td>
                        <td className="px-4 py-3 text-sm text-zinc-500">{new Date(tx.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 max-w-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full bg-[#00E5FF]/10 flex items-center justify-center">
                <User className="h-8 w-8 text-[#00E5FF]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{user?.name}</h3>
                <p className="text-sm text-zinc-500">{user?.email}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.name')}</Label>
                <Input value={profileForm.name} onChange={(e) => setProfileForm(p => ({ ...p, name: e.target.value }))}
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="profile-name-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.phone')}</Label>
                <Input value={profileForm.phone} onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="profile-phone-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.company')}</Label>
                <Input value={profileForm.company} onChange={(e) => setProfileForm(p => ({ ...p, company: e.target.value }))}
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="profile-company-input" />
              </div>
              <Button onClick={updateProfile} className="bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm h-11" data-testid="profile-save-btn">
                {t('common.save')}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="watchlist">
          {watchlist.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <Heart className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">Your watchlist is empty</p>
              <Button onClick={() => navigate('/domains')} className="mt-4 bg-[#00E5FF] text-black font-bold rounded-sm">Browse Domains</Button>
            </div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.domain')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.price')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Added</th>
                    <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr></thead>
                  <tbody>
                    {watchlist.map(item => (
                      <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-sm text-white font-medium">{item.domain?.full_name || item.domain_name}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">${(item.domain?.price || item.domain_price)?.toLocaleString()}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${item.domain?.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-800 text-zinc-500'}`}>{item.domain?.status || 'unknown'}</span></td>
                        <td className="px-4 py-3 text-sm text-zinc-500">{new Date(item.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right flex items-center justify-end gap-3">
                          <button onClick={() => navigate(`/domains/${item.domain_id}`)} className="text-xs text-[#00E5FF] hover:underline">
                            <ExternalLink className="h-3.5 w-3.5 inline mr-1" /> View
                          </button>
                          <button onClick={async () => {
                            try { await watchlistAPI.remove(item.domain_id); setWatchlist(prev => prev.filter(w => w.domain_id !== item.domain_id)); } catch {}
                          }} className="text-red-400 hover:text-red-300" data-testid={`remove-watchlist-${item.domain_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="notifications">
          {notifications.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <Bell className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No notifications yet</p>
            </div>
          ) : (
            <div>
              {unreadCount > 0 && (
                <div className="flex justify-end mb-3">
                  <Button variant="ghost" size="sm" className="text-xs text-zinc-500 hover:text-[#00E5FF]"
                    onClick={async () => { await notificationAPI.markAllRead(); setNotifications(prev => prev.map(n => ({...n, read: true}))); setUnreadCount(0); }}>
                    Mark all as read
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                {notifications.map(n => (
                  <div key={n.id} className={`p-4 bg-[#0A0A0A] border rounded-sm flex items-start gap-3 ${n.read ? 'border-white/5' : 'border-[#00E5FF]/20 bg-[#00E5FF]/[0.02]'}`}>
                    <Bell className={`h-4 w-4 mt-0.5 shrink-0 ${n.read ? 'text-zinc-600' : 'text-[#00E5FF]'}`} />
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium ${n.read ? 'text-zinc-400' : 'text-white'}`}>{n.title}</h4>
                      <p className="text-xs text-zinc-500 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    {!n.read && (
                      <button onClick={async () => { await notificationAPI.markRead(n.id); setNotifications(prev => prev.map(x => x.id === n.id ? {...x, read: true} : x)); setUnreadCount(c => Math.max(0, c - 1)); }}
                        className="text-[10px] text-zinc-500 hover:text-[#00E5FF]">Mark read</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
